import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, forwardRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MessageService } from 'base';
import { ConfigurationService } from '../../services/configuration.service';
import { OnDestroy } from '@angular/core';
import { ComponentBase } from 'common';
import { StringUtils } from 'DiscoveryEngine.TS/Utils';
import { Integration, IntegrationNames, IntegrationTable } from '../../../DiscoveryEngine.TS/Core/Integration';
import { DefaultIntegrationSettingsChild } from '../default-integration-settings-child-interface/default-integration-settings-child-interface';


@Component({
	selector: 'jira-default-settings',
	templateUrl: './jira-default-settings.component.html',
	styleUrls: ['./jira-default-settings.component.scss']
})
export class JiraDefaultSettingsComponent extends ComponentBase implements DefaultIntegrationSettingsChild, OnInit, OnDestroy, OnChanges {
	@Input()
	public inputSettings: Integration;

	public form: UntypedFormGroup;

	public enabled: UntypedFormControl;

	public serverURL: UntypedFormControl;
	public username: UntypedFormControl;
	public apiToken: UntypedFormControl;

	private apiTokenChanged: boolean;
	private apiTokenChangedSub: Subscription;

	constructor(private fb: UntypedFormBuilder,
		private configService: ConfigurationService,
		private messageService: MessageService) {

		super();
	}

	public ngOnInit(): void {
		this.buildForm();
	}

	public ngOnDestroy(): void {
		if (this.apiTokenChangedSub) {
			this.apiTokenChangedSub.unsubscribe();
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.buildForm();
	}

	private buildForm(): void {
		this.enabled = undefined;
		this.serverURL = undefined;
		this.username = undefined;
		this.apiToken = undefined;

		let enabled = true;
		let serverURL = "";
		let username = "";
		let apiToken = "";

		// Load in the settings from the input settings variable, and set them to the form controls.
		if (this.inputSettings) {
			enabled = this.inputSettings.Enabled;
			serverURL = this.inputSettings.ServerURL;
			username = this.inputSettings.Username;
			apiToken = (this.inputSettings.HasApiKey ? this.hidePassword(this.inputSettings.ApiKey) : "");
		}

		this.enabled = new UntypedFormControl(enabled);
		this.serverURL = new UntypedFormControl(serverURL);
		this.username = new UntypedFormControl(username);
		this.apiToken = new UntypedFormControl(apiToken);

		this.apiTokenChanged = false;
		this.apiTokenChangedSub = this.apiToken.valueChanges.subscribe(() => {
			this.apiTokenChanged = true;
		});

		this.form = this.fb.group({
			enabled: this.enabled,
			serverURL: this.serverURL,
			username: this.username,
			apiToken: this.apiToken,
		},
		{
			validator: Validators.compose([
				this.noneOrAllSettings(),
			])
		});
	}

	public enableSave(): boolean {
		return true;
	}

	public onSave(): void {
		const outputIntegrationTable = new IntegrationTable();
		const integrationSettings = this.getIntegrationSettings();
		outputIntegrationTable.Add(integrationSettings);
		this.configService.setIntegrationTable(outputIntegrationTable).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully saved Jira default settings");
				this.form.markAsPristine();
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to save Jira default settings");
			});
	}

	public get enableTestJiraButton(): boolean {
		return (!!this.serverURL && !!this.serverURL.value
			&& !!this.username && !!this.username.value
			&& !!this.apiToken && !!this.apiToken.value);
	}

	public testConnection(): void {
		let apiToken = "";
		if (this.apiTokenChanged) {
			apiToken = StringUtils.ObfuscateString2(this.apiToken.value);
		}
		else {
			apiToken = this.inputSettings.ApiKey;
		}
		this.configService.testJiraConnection(this.serverURL.value, this.username.value, apiToken).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully connected to Jira");
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to connect to Jira");
			});
	}

	public onPasswordKeyDownHandler(event: any): void {
		if (!this.apiTokenChanged) {
			this.apiTokenChanged = true;

			this.apiToken.setValue('');
			this.apiToken.markAsDirty();
		}
	}

	public getIntegrationSettings(): Integration {
		const outputIntegration = new Integration(IntegrationNames.Jira);
		outputIntegration.Enabled = this.enabled.value;
		outputIntegration.ServerURL = this.serverURL.value;
		outputIntegration.Username = this.username.value;
		if (this.apiTokenChanged) {
			outputIntegration.ApiKey = StringUtils.ObfuscateString2(this.apiToken.value);
		}
		else {
			outputIntegration.ApiKey = this.inputSettings.ApiKey;
		}

		return outputIntegration;
	}

	private noneOrAllSettings(): ValidatorFn {
		return () => {
			let b1 = (!!this.serverURL.value !== !!this.username.value);
			let b2 = (!!this.username.value !== !!this.apiToken.value);
			if (b1 || b2) {
				return {
					noneOrAllSettings: { valid: false }
				};
			}

			return null;
		}
	}

}
